import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';

import SelectFilter from '../filters/SelectFilter';
import { nullDataChartLabel } from '../nullData';
import type { CellInfo, ReportFieldType } from './types';

const getMetaRating = ({ fieldData, helpers }: CellInfo) => {
  // Scoring settings: use L/I matrix lookup when available
  if (
    helpers.hasScoringSettings &&
    fieldData.meta?.likelihood != null &&
    fieldData.meta?.impact != null
  ) {
    const result = helpers.getRatingByLikelihoodAndImpact(
      Number(fieldData.meta.likelihood),
      Number(fieldData.meta.impact)
    );
    if (result) {
      return { label: result.label, color: result.color };
    }
  }

  // Taxonomy fallback: use backend-provided label + color
  return {
    label: fieldData.value as string,
    color: fieldData.meta?.color as string | undefined,
  };
};

/**
 * Resolves a metaRating's chart label/colour from the field's own value (the
 * rating label produced by the backend) rather than from its likelihood/impact
 * meta.
 *
 * Charts group by the field value, but the returned `meta` (likelihood/impact)
 * belongs to an arbitrary representative row in the group and is not guaranteed
 * to match the group's value. Deriving the label from the L/I matrix (as
 * `getMetaRating` does) therefore produces phantom categories — e.g. a "Severe"
 * bar for a group that holds no Severe-rated rows (RSP-5958). The group key (the
 * value) already carries the correct label, and its colour is in `meta.color`.
 * Per-row table cells keep using `getMetaRating`, where the meta belongs to that
 * single row.
 */
const getMetaRatingFromValue = ({ fieldData }: CellInfo) => ({
  label: fieldData.value as string | null,
  color: fieldData.meta?.color as string | undefined,
});

export const metaRating: ReportFieldType = {
  asyncOptionSuggestions: true,
  getChartColor(cellData) {
    if (cellData.fieldDef.displayType !== 'metaRating') {
      throw new Error('metaRating field type used out of context');
    }

    return getMetaRatingFromValue(cellData).color;
  },
  exportVal: (cellData) => {
    if (cellData.fieldDef.displayType !== 'metaRating') {
      throw new Error('metaRating field type used out of context');
    }

    return (getMetaRating(cellData).label ?? '') as string;
  },
  getChartLabel: (cellData) => {
    if (cellData.fieldDef.displayType !== 'metaRating') {
      throw new Error('metaRating field type used out of context');
    }

    return (getMetaRatingFromValue(cellData).label ??
      nullDataChartLabel()) as string;
  },
  cell: function (cellData) {
    if (cellData.fieldDef.displayType !== 'metaRating') {
      throw new Error('metaRating field type used out of context');
    }

    const rating = getMetaRating(cellData);

    return (
      <SimpleRatingBadge
        rating={{ color: rating.color, label: rating.label }}
      />
    );
  },
  propertyConfig(field, helpers) {
    // Scoring settings: provide matrix-derived filter options
    if (helpers.hasScoringSettings && helpers.riskRatingOptions.length > 0) {
      return {
        key: field.key,
        groupValuesLabel: field.groupValuesLabel,
        propertyLabel: field.propertyLabel,
        operators: ['=', '!='].map((operator) => ({
          operator,
          format: (value) => {
            const match = helpers.riskRatingOptions.find(
              (o) => o.label === value
            );

            return match?.label ?? value;
          },
          form: (props) => {
            return (
              <SelectFilter
                {...props}
                options={helpers.riskRatingOptions.map((o) => ({
                  value: o.label,
                  label: o.label,
                }))}
              />
            );
          },
        })),
      };
    }

    // Taxonomy fallback: use async option suggestions
    return {
      key: field.key,
      groupValuesLabel: field.groupValuesLabel,
      propertyLabel: field.propertyLabel,
      operators: [
        {
          operator: '=',
        },
      ],
    };
  },
};
